import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

export const getMediaUrl = (path) => `${API_BASE_URL.replace(/\/api$/, '')}${path}`;

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Single-flight token refresh: if several requests 401 at once, they all
// await the same in-flight refresh instead of each firing their own
// POST /auth/refresh (which piles up redundant round trips and starves
// slower requests behind them).
let refreshPromise = null;

function refreshAccessToken() {
  if (!refreshPromise) {
    refreshPromise = (async () => {
      const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {}, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('refresh_token')}`,
        },
      });
      const { access_token } = response.data;
      localStorage.setItem('access_token', access_token);
      return access_token;
    })().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

// A 401 from the auth endpoints themselves is not an expired session: /auth/login
// returns 401 for a bad password, and /auth/refresh returns it for a dead refresh
// token. Both must reject so the caller can render the error, rather than being
// funnelled into the session-expiry handling below.
const isAuthEndpoint = (url = '') =>
  url.includes('/auth/login') || url.includes('/auth/refresh');

// flask-jwt-extended reserves 401 for a missing or expired token and answers a
// malformed or wrongly-signed one with 422 ("Signature verification failed"),
// which is what every stored token becomes if JWT_SECRET_KEY is rotated or lost.
// Its error bodies carry a `msg` key, while this app's own errors use `error` —
// including the receipt scanner's 422 — so the key is what tells a dead token
// apart from an ordinary validation failure we must leave to the caller.
const isJwtRejection = (response) =>
  response?.status === 422 &&
  typeof response.data?.msg === 'string' &&
  response.data?.error === undefined;

function logOutAndRedirect(error) {
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('user');
  // Guard against redirecting /login to itself, which a browser services as
  // a full document reload and so repeats forever.
  if (window.location.pathname !== '/login') {
    window.location.href = '/login';
  }
  return Promise.reject(error);
}

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // `error.config` is absent when the failure happened before a request was built
    // (a network error, an aborted navigation). Reading `.url` off it unguarded would
    // replace the real AxiosError with a TypeError from inside this handler.
    if (isAuthEndpoint(originalRequest?.url ?? '')) {
      return Promise.reject(error);
    }

    // A token the server will not even parse cannot be refreshed: the refresh
    // token was minted with the same secret, so it is dead for the same reason.
    // Log out directly instead of spending a round trip proving it.
    if (isJwtRejection(error.response)) {
      return logOutAndRedirect(error);
    }

    // If error is 401 and we haven't retried yet
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (!localStorage.getItem('refresh_token')) {
        // An access token with no refresh token is a broken session, not an anonymous
        // caller: nothing can mint a new token, so every request on the page 401s
        // forever behind a UI that still believes it is signed in — which is what
        // isAuthenticated reports, since it reads the `user` blob in localStorage and
        // never checks it against the server. Log out so the user lands on /login.
        if (localStorage.getItem('access_token')) {
          return logOutAndRedirect(error);
        }
        // With neither token the caller genuinely was never logged in — the public
        // share page, say. Reject and let it handle the 401; redirecting would bounce
        // an already-anonymous page onto /login for no reason.
        return Promise.reject(error);
      }

      originalRequest._retry = true;

      try {
        const access_token = await refreshAccessToken();
        originalRequest.headers.Authorization = `Bearer ${access_token}`;
        return api(originalRequest);
      } catch (refreshError) {
        return logOutAndRedirect(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  register: (userData) => api.post('/auth/register', userData),
  getCurrentUser: () => api.get('/auth/me'),
  changePassword: (passwords) => api.post('/auth/change-password', passwords),
  // Username persistence methods
  saveUsername: (username) => {
    if (username) {
      localStorage.setItem('saved_username', username);
    }
  },
  getSavedUsername: () => {
    return localStorage.getItem('saved_username') || '';
  },
  clearSavedUsername: () => {
    localStorage.removeItem('saved_username');
  },
};

// Horses API
export const horsesAPI = {
  getAll: (params) => api.get('/horses', { params }),
  getById: (id) => api.get(`/horses/${id}`),
  create: (data) => api.post('/horses', data),
  update: (id, data) => api.put(`/horses/${id}`, data),
  delete: (id) => api.delete(`/horses/${id}`),
  getStats: () => api.get('/horses/stats'),
  // Pedigree API
  getPedigree: (id) => api.get(`/horses/${id}/pedigree`),
  getPedigreeDisplay: (id) => api.get(`/horses/${id}/pedigree/display`),
  savePedigree: (id, html) => api.post(`/horses/${id}/pedigree`, { pedigree: html }),
  updatePedigree: (id, html) => api.put(`/horses/${id}/pedigree`, { pedigree: html }),
  deletePedigree: (id) => api.delete(`/horses/${id}/pedigree`),
  // Pulls the chart from allbreedpedigree.com when the horse has none stored.
  // Drives a headless browser server-side, so expect 15-40s. Pass { slug } to
  // answer a 'multiple' result, { force: true } to replace what's stored.
  fetchPedigreeFromAllbreed: (id, body = {}) =>
    api.post(`/horses/${id}/pedigree/fetch`, body),
  // Photo API
  uploadPhoto: (id, file, caption = '', isHero = false) => {
    const form = new FormData();
    form.append('photo', file);
    if (caption) form.append('caption', caption);
    if (isHero) form.append('is_hero', 'true');
    return api.post(`/horses/${id}/photos`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  updatePhoto: (horseId, photoId, data) => api.put(`/horses/${horseId}/photos/${photoId}`, data),
  deletePhoto: (horseId, photoId) => api.delete(`/horses/${horseId}/photos/${photoId}`),
};

// Breeding API
export const breedingAPI = {
  getAll: (params) => api.get('/breeding', { params }),
  getById: (id) => api.get(`/breeding/${id}`),
  create: (data) => api.post('/breeding', data),
  update: (id, data) => api.put(`/breeding/${id}`, data),
  delete: (id) => api.delete(`/breeding/${id}`),
  getStats: () => api.get('/breeding/stats'),
  getMareGestationHistory: (mareId) => api.get(`/breeding/mare/${mareId}/gestation-history`),
  getHypothetical: (sireId, damId) =>
    api.get(`/breeding/hypothetical`, {
      params: { sire_id: sireId, dam_id: damId },
    }),

  // Public share links for a hypothetical cross. getShare resolves to null when
  // the pair has no un-revoked link.
  getShare: (sireId, damId) =>
    api.get(`/breeding/hypothetical/share`, {
      params: { sire_id: sireId, dam_id: damId },
    }),
  createShare: (data) => api.post(`/breeding/hypothetical/share`, data),
  revokeShare: (shareId) => api.delete(`/breeding/hypothetical/share/${shareId}`),
};

// Unauthenticated endpoints. Consumed by pages that render for visitors with no
// session — the axios instance already rejects a 401 instead of redirecting when
// there is no refresh token, so these are safe to call anonymously.
export const publicAPI = {
  getSharedPedigree: (token) => api.get(`/public/shared-pedigree/${token}`),
};

// Possible foal names for a breeding record
export const breedingFoalNamesAPI = {
  getAll: (recordId) => api.get(`/breeding/${recordId}/foal-names`),
  create: (recordId, data) => api.post(`/breeding/${recordId}/foal-names`, data),
  update: (recordId, nameId, data) => api.put(`/breeding/${recordId}/foal-names/${nameId}`, data),
  delete: (recordId, nameId) => api.delete(`/breeding/${recordId}/foal-names/${nameId}`),
};

// Users API (admin only)
export const usersAPI = {
  getAll: (params) => api.get('/users', { params }),
  getById: (id) => api.get(`/users/${id}`),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
  setPassword: (id, password) => api.post(`/users/${id}/set-password`, { password }),
  getAlertTypes: () => api.get('/users/alert-types'),
};

// Mare Owners API
export const mareOwnersAPI = {
  getAll: (params) => api.get('/mare-owners', { params }),
  getById: (id) => api.get(`/mare-owners/${id}`),
  create: (data) => api.post('/mare-owners', data),
  update: (id, data) => api.put(`/mare-owners/${id}`, data),
  delete: (id) => api.delete(`/mare-owners/${id}`),
};

// Vets API
export const vetsAPI = {
  getAll: (params) => api.get('/vets', { params }),
  getById: (id) => api.get(`/vets/${id}`),
  create: (data) => api.post('/vets', data),
  update: (id, data) => api.put(`/vets/${id}`, data),
  delete: (id) => api.delete(`/vets/${id}`),
};

// Trainers API
export const trainersAPI = {
  getAll: (params) => api.get('/trainers', { params }),
  getById: (id) => api.get(`/trainers/${id}`),
  create: (data) => api.post('/trainers', data),
  update: (id, data) => api.put(`/trainers/${id}`, data),
  delete: (id) => api.delete(`/trainers/${id}`),
};

// Appointment Types API
export const appointmentTypesAPI = {
  getAll: (params) => api.get('/appointment-types', { params }),
  getById: (id) => api.get(`/appointment-types/${id}`),
  create: (data) => api.post('/appointment-types', data),
  update: (id, data) => api.put(`/appointment-types/${id}`, data),
  delete: (id) => api.delete(`/appointment-types/${id}`),
};

// Appointments API
export const appointmentsAPI = {
  // Vet appointments
  getVetAppointments: (params) => api.get('/appointments/vet', { params }),
  getVetAppointmentById: (id) => api.get(`/appointments/vet/${id}`),
  createVetAppointment: (data) => api.post('/appointments/vet', data),
  updateVetAppointment: (id, data) => api.put(`/appointments/vet/${id}`, data),
  deleteVetAppointment: (id) => api.delete(`/appointments/vet/${id}`),
  getUpcomingVetAppointments: () => api.get('/appointments/vet/upcoming'),
  
  // Pregnancy shots
  getPregnancyShots: (params) => api.get('/appointments/pregnancy-shots', { params }),
  getPregnancyShotById: (id) => api.get(`/appointments/pregnancy-shots/${id}`),
  createPregnancyShot: (data) => api.post('/appointments/pregnancy-shots', data),
  updatePregnancyShot: (id, data) => api.put(`/appointments/pregnancy-shots/${id}`, data),
  deletePregnancyShot: (id) => api.delete(`/appointments/pregnancy-shots/${id}`),
  getUpcomingShots: () => api.get('/appointments/pregnancy-shots/upcoming'),
};

// Feed API
export const feedAPI = {
  getAll: (params) => api.get('/feed', { params }),
  getById: (id) => api.get(`/feed/${id}`),
  create: (data) => api.post('/feed', data),
  update: (id, data) => api.put(`/feed/${id}`, data),
  delete: (id) => api.delete(`/feed/${id}`),
  getSummary: () => api.get('/feed/summary'),
};

// Inventory API
export const inventoryAPI = {
  getAll: (params) => api.get('/inventory', { params }),
  getById: (id) => api.get(`/inventory/${id}`),
  create: (data) => api.post('/inventory', data),
  update: (id, data) => api.put(`/inventory/${id}`, data),
  delete: (id) => api.delete(`/inventory/${id}`),
  getLowStock: () => api.get('/inventory/low-stock'),
  
  // Categories
  getCategories: () => api.get('/inventory/categories'),
  createCategory: (data) => api.post('/inventory/categories', data),
  updateCategory: (id, data) => api.put(`/inventory/categories/${id}`, data),
  deleteCategory: (id) => api.delete(`/inventory/categories/${id}`),

  // Item Types
  getItemTypes: () => api.get('/inventory/item-types'),
  createItemType: (data) => api.post('/inventory/item-types', data),
  updateItemType: (id, data) => api.put(`/inventory/item-types/${id}`, data),
  deleteItemType: (id) => api.delete(`/inventory/item-types/${id}`),

  // Units
  getUnits: () => api.get('/inventory/units'),
  createUnit: (data) => api.post('/inventory/units', data),
  updateUnit: (id, data) => api.put(`/inventory/units/${id}`, data),
  deleteUnit: (id) => api.delete(`/inventory/units/${id}`),

  // Suppliers
  getSuppliers: () => api.get('/inventory/suppliers'),
  createSupplier: (data) => api.post('/inventory/suppliers', data),
  updateSupplier: (id, data) => api.put(`/inventory/suppliers/${id}`, data),
  deleteSupplier: (id) => api.delete(`/inventory/suppliers/${id}`),

  // Manufacturers
  getManufacturers: () => api.get('/inventory/manufacturers'),
  createManufacturer: (data) => api.post('/inventory/manufacturers', data),
  updateManufacturer: (id, data) => api.put(`/inventory/manufacturers/${id}`, data),
  deleteManufacturer: (id) => api.delete(`/inventory/manufacturers/${id}`),
};

// Expenses API
export const expensesAPI = {
  getAll: (params) => api.get('/expenses', { params }),
  getById: (id) => api.get(`/expenses/${id}`),
  create: (data) => api.post('/expenses', data),
  update: (id, data) => api.put(`/expenses/${id}`, data),
  delete: (id) => api.delete(`/expenses/${id}`),
  getSummary: (params) => api.get('/expenses/summary', { params }),
  getSummaryByYear: () => api.get('/expenses/summary/by-year'),
  getTotal: (params) => api.get('/expenses/total', { params }),
  
  // Receipt scanner
  scanReceipt: (file) => {
    const form = new FormData();
    form.append('receipt', file);
    return api.post('/expenses/scan-receipt', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  // Fire-and-forget: the phone's own timings, which the server cannot see.
  logScanTiming: (payload) => api.post('/expenses/scan-receipt/timing', payload),
  uploadReceipt: (expenseId, file) => {
    const form = new FormData();
    form.append('receipt', file);
    return api.post(`/expenses/${expenseId}/receipt`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  // Categories
  getCategories: () => api.get('/expenses/categories'),
  createCategory: (data) => api.post('/expenses/categories', data),
  updateCategory: (id, data) => api.put(`/expenses/categories/${id}`, data),
  deleteCategory: (id) => api.delete(`/expenses/categories/${id}`),
};

// Reports API
export const reportsAPI = {
  getTaxYears: () => api.get('/reports/tax-years'),
  // Must go through the axios instance: auth is a Bearer header, not a cookie,
  // so a plain <a href> or window.open to this endpoint would 401.
  downloadTaxYear: (year) =>
    api.get(`/reports/tax-year/${year}`, { responseType: 'blob' }),
};

// Emailed and texted receipts awaiting review
export const pendingReceiptsAPI = {
  getAll: (params) => api.get('/pending-receipts', { params }),
  dismiss: (id) => api.post(`/pending-receipts/${id}/dismiss`),
  approve: (id, data) => api.post(`/pending-receipts/${id}/approve`, data),
};

// Personal Inbox Filters API
export const personalInboxFiltersAPI = {
  getAll: () => api.get('/personal-inbox-filters'),
  create: (data) => api.post('/personal-inbox-filters', data),
  update: (id, data) => api.put(`/personal-inbox-filters/${id}`, data),
  delete: (id) => api.delete(`/personal-inbox-filters/${id}`),
};

// Income API
export const incomeAPI = {
  getAll: (params) => api.get('/income', { params }),
  getById: (id) => api.get(`/income/${id}`),
  create: (data) => api.post('/income', data),
  update: (id, data) => api.put(`/income/${id}`, data),
  delete: (id) => api.delete(`/income/${id}`),
  getSummary: (params) => api.get('/income/summary', { params }),
  getTotal: (params) => api.get('/income/total', { params }),
  
  // Categories
  getCategories: () => api.get('/income/categories'),
  createCategory: (data) => api.post('/income/categories', data),
  updateCategory: (id, data) => api.put(`/income/categories/${id}`, data),
  deleteCategory: (id) => api.delete(`/income/categories/${id}`),
};

// Reference API
export const referenceAPI = {
  // Horse Types
  getHorseTypes: () => api.get('/reference/horse-types'),
  createHorseType: (data) => api.post('/reference/horse-types', data),
  
  // Breeds
  getBreeds: () => api.get('/reference/breeds'),
  createBreed: (data) => api.post('/reference/breeds', data),
  
  // Colors
  getColors: () => api.get('/reference/colors'),
  createColor: (data) => api.post('/reference/colors', data),
  
  // Genders
  getGenders: () => api.get('/reference/genders'),
  createGender: (data) => api.post('/reference/genders', data),
  
  // Locations
  getLocations: () => api.get('/reference/locations'),
  createLocation: (data) => api.post('/reference/locations', data),
  
  // Breeding Methods
  getBreedingMethods: () => api.get('/reference/breeding-methods'),
  createBreedingMethod: (data) => api.post('/reference/breeding-methods', data),
  
  // Pregnancy Statuses
  getPregnancyStatuses: () => api.get('/reference/pregnancy-statuses'),
  createPregnancyStatus: (data) => api.post('/reference/pregnancy-statuses', data),
  
  // Registration Organizations
  getRegistrationOrgs: () => api.get('/reference/registration-orgs'),
  createRegistrationOrg: (data) => api.post('/reference/registration-orgs', data),
  
  // Feed Types
  getFeedTypes: () => api.get('/reference/feed-types'),
  createFeedType: (data) => api.post('/reference/feed-types', data),
  
  // Feed Brands
  getFeedBrands: (feedTypeId) => api.get('/reference/feed-brands', { params: { feed_type_id: feedTypeId } }),
  createFeedBrand: (data) => api.post('/reference/feed-brands', data),
  
  // Feed Names
  getFeedNames: (brandId) => api.get('/reference/feed-names', { params: { brand_id: brandId } }),
  createFeedName: (data) => api.post('/reference/feed-names', data),
  
  // Show Events
  getShowEvents: () => api.get('/reference/show-events'),
  createShowEvent: (data) => api.post('/reference/show-events', data),
  
  // Show Names
  getShowNames: () => api.get('/reference/show-names'),
  createShowName: (data) => api.post('/reference/show-names', data),
  
  // Show Locations
  getShowLocations: () => api.get('/reference/show-locations'),
  createShowLocation: (data) => api.post('/reference/show-locations', data),
  
  // Show Classes
  getShowClasses: () => api.get('/reference/show-classes'),
  createShowClass: (data) => api.post('/reference/show-classes', data),
  
  // Show Divisions
  getShowDivisions: () => api.get('/reference/show-divisions'),
  createShowDivision: (data) => api.post('/reference/show-divisions', data),
  
  // Expense Type Categories
  getExpenseTypeCategories: () => api.get('/reference/expense-type-categories'),
  createExpenseTypeCategory: (data) => api.post('/reference/expense-type-categories', data),
  updateExpenseTypeCategory: (id, data) => api.put(`/reference/expense-type-categories/${id}`, data),
  deleteExpenseTypeCategory: (id) => api.delete(`/reference/expense-type-categories/${id}`),
  
  // Expense Types
  getExpenseTypes: () => api.get('/reference/expense-types'),
  createExpenseType: (data) => api.post('/reference/expense-types', data),
  updateExpenseType: (id, data) => api.put(`/reference/expense-types/${id}`, data),
  deleteExpenseType: (id) => api.delete(`/reference/expense-types/${id}`),
  
  // Expense Location Names
  getExpenseLocationNames: () => api.get('/reference/expense-location-names'),
  createExpenseLocationName: (data) => api.post('/reference/expense-location-names', data),
  updateExpenseLocationName: (id, data) => api.put(`/reference/expense-location-names/${id}`, data),
  deleteExpenseLocationName: (id) => api.delete(`/reference/expense-location-names/${id}`),
  
  // Expense Locations
  getExpenseLocations: () => api.get('/reference/expense-locations'),
  createExpenseLocation: (data) => api.post('/reference/expense-locations', data),
  updateExpenseLocation: (id, data) => api.put(`/reference/expense-locations/${id}`, data),
  deleteExpenseLocation: (id) => api.delete(`/reference/expense-locations/${id}`),
  
  // Income Type Categories
  getIncomeTypeCategories: () => api.get('/reference/income-type-categories'),
  createIncomeTypeCategory: (data) => api.post('/reference/income-type-categories', data),
  updateIncomeTypeCategory: (id, data) => api.put(`/reference/income-type-categories/${id}`, data),
  deleteIncomeTypeCategory: (id) => api.delete(`/reference/income-type-categories/${id}`),
  
  // Income Types
  getIncomeTypes: () => api.get('/reference/income-types'),
  createIncomeType: (data) => api.post('/reference/income-types', data),
  updateIncomeType: (id, data) => api.put(`/reference/income-types/${id}`, data),
  deleteIncomeType: (id) => api.delete(`/reference/income-types/${id}`),
  
  // Income Location Names
  getIncomeLocationNames: () => api.get('/reference/income-location-names'),
  createIncomeLocationName: (data) => api.post('/reference/income-location-names', data),
  updateIncomeLocationName: (id, data) => api.put(`/reference/income-location-names/${id}`, data),
  deleteIncomeLocationName: (id) => api.delete(`/reference/income-location-names/${id}`),
  
  // Income Locations
  getIncomeLocations: () => api.get('/reference/income-locations'),
  createIncomeLocation: (data) => api.post('/reference/income-locations', data),
  updateIncomeLocation: (id, data) => api.put(`/reference/income-locations/${id}`, data),
  deleteIncomeLocation: (id) => api.delete(`/reference/income-locations/${id}`),

  // Mileage Rates (IRS standard mileage rate, one row per tax year)
  getMileageRates: () => api.get('/reference/mileage-rates'),
  createMileageRate: (data) => api.post('/reference/mileage-rates', data),
  updateMileageRate: (id, data) => api.put(`/reference/mileage-rates/${id}`, data),
  deleteMileageRate: (id) => api.delete(`/reference/mileage-rates/${id}`),
};

// Horse Registrations API
export const horseRegistrationsAPI = {
  getAll: (horseId) => api.get(`/horses/${horseId}/registrations`),
  create: (horseId, data) => api.post(`/horses/${horseId}/registrations`, data),
  update: (horseId, registrationId, data) => api.put(`/horses/${horseId}/registrations/${registrationId}`, data),
  delete: (horseId, registrationId) => api.delete(`/horses/${horseId}/registrations/${registrationId}`),
};

// Horse Payments API (deposits/installments toward a horse's sale or purchase)
export const horsePaymentsAPI = {
  getAll: (horseId, params) => api.get(`/horses/${horseId}/payments`, { params }),
  create: (horseId, data) => api.post(`/horses/${horseId}/payments`, data),
  update: (horseId, paymentId, data) => api.put(`/horses/${horseId}/payments/${paymentId}`, data),
  delete: (horseId, paymentId) => api.delete(`/horses/${horseId}/payments/${paymentId}`),
};

// Equine Sales Agreement: saved buyer/terms per horse, and the PDF built from them
export const saleAgreementAPI = {
  get: (horseId) => api.get(`/horses/${horseId}/sale-agreement`),
  save: (horseId, data) => api.put(`/horses/${horseId}/sale-agreement`, data),
  // Through the axios instance for the Bearer header — see reportsAPI.downloadTaxYear.
  downloadPdf: (horseId) =>
    api.get(`/horses/${horseId}/sale-agreement/pdf`, { responseType: 'blob' }),
};

// Business API
export const businessAPI = {
  get: () => api.get('/business'),
  update: (data) => api.put('/business', data),
};

// Health API
export const healthAPI = {
  checkBackend: () => api.get('/health'),
};

// Shows API
export const showsAPI = {
  getAll: (params) => api.get('/shows', { params }),
  getById: (id) => api.get(`/shows/${id}`),
  create: (data) => api.post('/shows', data),
  update: (id, data) => api.put(`/shows/${id}`, data),
  delete: (id) => api.delete(`/shows/${id}`),
  getStats: () => api.get('/shows/stats'),
};

// Ranch Assistant (NL -> SQL -> answer)
export const ranchAssistantAPI = {
  ask: (payload) => api.post('/ranch-assistant/ask', payload),
  health: () => api.get('/ranch-assistant/health'),
  getTtsVoices: () => api.get('/ranch-assistant/tts/voices'),
  speakTts: (payload) => api.post('/ranch-assistant/tts/speak', payload, { responseType: 'blob' }),
};

// AllBreed Submission Agent
export const allbreedAPI = {
  getQueue: () => api.get('/allbreed/queue'),
  getHistory: () => api.get('/allbreed/history'),
  getAuthStatus: () => api.get('/allbreed/auth-status'),
  eligibility: (horseId) => api.get(`/allbreed/${horseId}/eligibility`),
  resolve: (horseId) => api.post(`/allbreed/${horseId}/resolve`),
  confirmMatch: (horseId, slug) => api.post(`/allbreed/${horseId}/confirm-match`, { slug }),
  selectParents: (horseId, data) => api.post(`/allbreed/${horseId}/select-parents`, data),
  approve: (horseId) => api.post(`/allbreed/${horseId}/approve`),
  skip: (horseId) => api.post(`/allbreed/${horseId}/skip`),
  submit: (horseId) => api.post(`/allbreed/${horseId}/submit`),
};

// Audit Log (admin only)
export const auditLogAPI = {
  getAll: (params) => api.get('/audit-logs', { params }),
  getTables: () => api.get('/audit-logs/tables'),
};

// Geocoding — proxied through the backend so the Google Maps key stays
// server-side. Never call maps.googleapis.com directly from the client.
export const geocodeAPI = {
  lookup: (address) => api.post('/geocode', { address }),
};

// Foal waitlist (admin only). Signups are created by the public site at
// POST /api/public/waitlist; these are the review operations.
export const waitlistAPI = {
  getAll: (params) => api.get('/waitlist', { params }),
  updateStatus: (id, status) => api.put(`/waitlist/${id}`, { status }),
  delete: (id) => api.delete(`/waitlist/${id}`),
};

// Shared external links rendered as a sidebar section. Reads are open to any
// logged-in user; writes are admin-only (see backend/routes/useful_links.py).
export const usefulLinksAPI = {
  getAll: () => api.get('/useful-links'),
  create: (data) => api.post('/useful-links', data),
  update: (id, data) => api.put(`/useful-links/${id}`, data),
  delete: (id) => api.delete(`/useful-links/${id}`),
};

// Worker time clock. me/clockIn/clockOut are the worker's own punches and are
// the only ones the inventory_counter role may reach; the rest are admin-only.
export const timeClockAPI = {
  me: () => api.get('/time-clock/me'),
  clockIn: () => api.post('/time-clock/clock-in'),
  clockOut: () => api.post('/time-clock/clock-out'),
  getWorkers: () => api.get('/time-clock/workers'),
  updateEntry: (id, data) => api.put(`/time-clock/entries/${id}`, data),
  deleteEntry: (id) => api.delete(`/time-clock/entries/${id}`),
  markPaid: (userId, data) => api.post(`/time-clock/workers/${userId}/payments`, data),
  getPayments: (params) => api.get('/time-clock/payments', { params }),
  deletePayment: (id) => api.delete(`/time-clock/payments/${id}`),
};

export default api;

